import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useTime from '../../hooks/useTime';
import useFirebaseData from '../../hooks/useFirebaseData';
import useStravaStats from '../../hooks/useStravaStats';
import { database } from '../../configuration';
import { ThemeContext } from '../../context/ThemeContext';
import {
  InstagramOutlined,
  TwitterOutlined,
  GithubOutlined,
  LinkedinFilled,
  SunOutlined,
  MoonOutlined,
} from '@ant-design/icons';
import './Header.css';

const iconComponents = {
  GithubOutlined: <GithubOutlined />,
  LinkedinFilled: <LinkedinFilled />,
  TwitterOutlined: <TwitterOutlined />,
  InstagramOutlined: <InstagramOutlined />,
};

const Header = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [temperature, setTemperature] = useState(null);

  const { data: headerData, loading: headerLoading, error: headerError } = 
    useFirebaseData(database, '/header');

  const { data: apiKey, loading: keyLoading, error: keyError } =
    useFirebaseData(database, '/apiKeys/openWeatherMap');

  const { totalKm } = useStravaStats();

  const currentTime = useTime('Asia/Kolkata');

  useEffect(() => {
    if (!apiKey || !headerData?.weatherCity) {
      return;
    }

    const fetchWeather = async () => {
      const url = `https://api.openweathermap.org/data/2.5/weather?q=${headerData.weatherCity}&appid=${apiKey}&units=metric`;
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Weather data not found');
        }
        const data = await response.json();
        setTemperature(Math.round(data.main.temp));
      } catch (error) {
        console.error("Failed to fetch weather:", error);
        setTemperature(null);
      }
    };

    fetchWeather();
  }, [apiKey, headerData]);

  const socialIcons = useMemo(() => {
    if (!headerData?.socials) return [];
    
    return headerData.socials.map(social => ({
      ...social,
      icon: iconComponents[social.icon] || null,
    }));
  }, [headerData]);

  const handleLinkClick = (link) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  if (headerLoading || keyLoading) {
    return <header className="app-header"><div>Loading...</div></header>;
  }

  if (headerError || keyError || !headerData) {
    return <header className="app-header"><div>Error loading configuration.</div></header>;
  }
  
  const { websiteName, location, timeZoneAbbr } = headerData;

  return (
    <header className="app-header">
      <div className="header-left">
        <span className="logo" onClick={() => navigate('/')}>
          {websiteName}
        </span>
        <div className="header-info">
          <span className="separator-dot">•</span>
          <span className="header-item">{location}</span>
          
          <span className="header-item temperature">
            {temperature !== null ? `${temperature}°C` : '--°C'}
          </span>
          
          <span className="separator-dot">•</span>
          <span className="header-item time">
            {currentTime.toLowerCase()} {timeZoneAbbr}
          </span>

          {totalKm !== null && (
            <>
              <span className="separator-dot">•</span>
              <span className="header-item strava-stat">
                <svg viewBox="0 0 24 24" className="strava-icon" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                </svg>
                {totalKm} km
              </span>
            </>
          )}
        </div>
      </div>

      <div className="header-right">
        <div className="social-icons">
          {socialIcons.map((social) => (
            <div
              key={social.id}
              className="social-icon"
              onClick={() => handleLinkClick(social.link)}
            >
              {social.icon}
            </div>
          ))}
        </div>
        <div className="theme-switch" onClick={toggleTheme}>
          <div className={`switch-thumb ${theme}`}>
            {theme === 'dark' ? <MoonOutlined /> : <SunOutlined />}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
